import axiosClient from "@/shared/lib/axios/axios.ts";
import { ApiResponse } from "@/shared/types/api.ts";
import { SettingsContext, GetSettingsParams } from "../../types/settings.ts";
import { API_ROUTES } from "../config.ts";
import { Machine } from "@/machines/types/machine.ts";

// API response type (before transformation)
interface SettingsContextApiResponse {
  scope: SettingsContext["scope"];
  data: {
    organization: SettingsContext["data"]["organization"];
    locations: SettingsContext["data"]["locations"];
    machines: Array<Omit<Machine, "last_seen"> & { last_seen: string | null }>;
  };
}

/**
 * Transform machine dates from ISO strings to Date objects
 */
const transformSettingsContext = (apiResponse: SettingsContextApiResponse): SettingsContext => {
  return {
    scope: apiResponse.scope,
    data: {
      organization: apiResponse.data.organization,
      locations: apiResponse.data.locations,
      machines: apiResponse.data.machines.map((machine) => ({
        ...machine,
        last_seen: machine.last_seen ? new Date(machine.last_seen) : null,
      })),
    },
  };
};

/**
 * Get settings context based on scope (organization/location/machine)
 * @param params - Query parameters for scope and id
 * @returns Settings context with organization, locations, and machines
 */
export const getSettings = async (params?: GetSettingsParams) => {
  return axiosClient
    .get<ApiResponse<SettingsContextApiResponse>>(API_ROUTES.SETTINGS, {
      params,
    })
    .then((res) => ({
      ...res.data,
      data: transformSettingsContext(res.data.data),
    }));
};

/** PATCH /api/settings/locations/{id}/ — send only dirty fields (name and/or timezone). */
export interface UpdateLocationSettingsInput {
  id: number;
  /** Non-empty string; never null. */
  name?: string;
  /** Canonical IANA timezone, or null to clear the configured timezone. */
  timezone?: string | null;
}

export interface LocationSettingsUpdateResult {
  location_id: number;
  name: string;
  timezone: string | null;
}

export const updateLocationSettings = async ({ id, ...body }: UpdateLocationSettingsInput) => {
  return axiosClient
    .patch<ApiResponse<LocationSettingsUpdateResult>>(`${API_ROUTES.LOCATION_SETTINGS}${id}/`, body)
    .then((res) => res.data);
};
